import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Chip, Dropdown, Icon, Input, SegmentedControl, Textarea } from '@column/column-ui-kit';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { ROUTE } from '~/app/routes';
import { HiddenInput, RenderFields, RestrictedBanner } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { EventType, EventTypes, Webhook, WebhookRepository, WebhookVerify } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { EditToolbar, Flex, FormText, Inner } from '~/styles';
import { ApiError } from '~/typings/API';

import { WebhookResultModal } from './_components/WebhookResultModal';

interface Params {
  id: string;
  URLType: string;
}

type WebhookFormValues = Omit<Webhook, 'id' | 'createdAt' | 'updatedAt'> & {
  type: string;
  temporaryData?: {
    testEventType?: string;
    webhookResult?: WebhookVerify;
  };
};

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-self: end;
  justify-self: start;
`;

const StyledSegmentedControl = styled(SegmentedControl)`
  max-height: 40px;
`;

export const PageWebhooksEdit: React.FC = () => {
  const { currentPermission, currentUser, currentPlatform } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id, URLType } = useParams<keyof Params>() as Params;
  const isCreate = !id;
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [isWebhookResultModalOpen, setIsWebhookResultModalOpen] = useState<boolean>(false);
  const [modalTestEventType, setModalTestEventType] = useState<string>('');
  const [allEvents, setAllEvents] = useState<boolean>(true);
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'page' });
  const createdAtRef = useRef<string | undefined>(undefined);

  const eventTypes = useMemo(
    () => Object.values(EventTypes).filter((name: string) => !['UNRECOGNIZED'].includes(name)),
    []
  );

  const wildcards = useMemo(
    () => [
      ...new Set(
        eventTypes
          .map((name: string) => {
            const elements = name.split('.');
            const array: string[] = [];
            if (typeof elements[1] !== 'undefined') array.push(`${elements[0]}.*`);
            if (typeof elements[2] !== 'undefined') array.push(`${elements[0]}.${elements[1]}.*`);
            return array;
          })
          .flat()
      ),
    ],
    [eventTypes]
  );

  const formMethods = useForm<WebhookFormValues>({
    defaultValues: {
      url: '',
      description: '',
      enabledEvents: ['*'],
      isDisabled: false,
      secret: '',
      type: 'sandbox',
      temporaryData: {},
    },
  });
  const { handleSubmit, setValue, reset, getValues, watch } = formMethods;
  const temporaryData = watch('temporaryData') ?? {};
  useBreadcrumbTitle(id ? webhookUrl : null);

  const enabledEvents = watch('enabledEvents') ?? [];

  const testableEvents = useMemo(
    () =>
      (allEvents
        ? eventTypes
        : [
            ...new Set(
              enabledEvents
                .filter((eventId: string) => eventId !== '*')
                .flatMap((eventId: string) => {
                  if (eventId.includes('*')) {
                    return eventTypes.filter((name: string) => name.startsWith(eventId.replace('*', '')));
                  }
                  return eventId;
                })
            ),
          ]
      ).map((name: string) => ({
        label: name,
        value: name,
      })),
    [allEvents, eventTypes, enabledEvents]
  );

  const availableEvents = useMemo(
    () =>
      [...eventTypes, ...wildcards].sort().filter((eventId: string) => {
        const currentEnabledEvents = getValues('enabledEvents') ?? [];
        return !currentEnabledEvents.includes(eventId) && !currentEnabledEvents.includes(`${eventId.split('.')[0]}.*`);
      }),
    [eventTypes, wildcards, getValues]
  );

  const fetchData = React.useCallback(() => {
    WebhookRepository.get(id, URLType === 'Sandbox')
      .then((response) => {
        setWebhookUrl(response.url ?? '');
        reset({
          url: response.url ?? '',
          description: response.description ?? '',
          enabledEvents: response.enabledEvents ?? ['*'],
          isDisabled: response.isDisabled ?? false,
          secret: response.secret ?? '',
          type: URLType?.toLowerCase() ?? 'sandbox',
        });
        setAllEvents(!!response.enabledEvents?.includes('*'));
        createdAtRef.current = response.createdAt;
      })
      .catch((error: unknown) => {
        navigate(ROUTE.PLATFORM_WEBHOOKS);
        addDangerNotification({ content: (error as Error).message });
      });
  }, [id, URLType, reset, navigate, addDangerNotification]);

  useEffect(() => {
    if (id && currentUser) fetchData();
  }, [id, currentUser, fetchData]);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        () => {
          if (id && currentUser) fetchData();
        }
      ),
    [id, currentUser, fetchData]
  );

  const handleModalOpen = (eventType: string, webhookResultProps?: WebhookVerify) => {
    setModalTestEventType(eventType);
    if (webhookResultProps) {
      setValue('temporaryData', { ...temporaryData, webhookResult: webhookResultProps });
    }
    setIsWebhookResultModalOpen(true);
  };

  const handleSave = React.useCallback(
    (data: WebhookFormValues, callback?: () => void) => {
      if (!id || !currentUser) return;
      WebhookRepository.update(
        id,
        {
          description: typeof data.description === 'string' ? data.description : '',
          enabledEvents: allEvents
            ? ['*']
            : Array.isArray(data.enabledEvents)
              ? data.enabledEvents.filter((eventId) => eventId !== '*')
              : [],
          isDisabled: !!data.isDisabled,
          url: typeof data.url === 'string' ? data.url : '',
        },
        URLType === 'Sandbox'
      )
        .then(() => {
          if (callback) {
            callback();
            return;
          }
          addSuccessNotification({ content: 'Webhook saved' });
        })
        .catch((error: ApiError) => {
          if (error?.message) {
            addDangerNotification({
              content: error.message,
              actionButton: error.documentationUrl
                ? {
                    label: 'Docs',
                    onClick: () => window.open(error.documentationUrl, '_blank'),
                  }
                : undefined,
            });
          } else if (error instanceof Error) {
            addDangerNotification({ content: error.message });
          } else {
            addDangerNotification({ content: 'Unknown error' });
          }
        });
    },
    [id, currentUser, allEvents, URLType, addSuccessNotification, addDangerNotification]
  );

  const onSubmit = (data: WebhookFormValues) => {
    if (!currentUser) return;
    if (id) {
      handleSave(data);
      return;
    }

    WebhookRepository.create(
      {
        description: data.description ?? '',
        enabledEvents: allEvents ? ['*'] : (data.enabledEvents ?? []),
        url: data.url ?? '',
      },
      data.type === 'sandbox'
    )
      .then((response) => {
        navigate(
          `${ROUTE.PLATFORM_WEBHOOKS}/edit/${data.type === 'sandbox' ? 'Sandbox' : 'Production'}/${response.id}`
        );
        addSuccessNotification({ content: 'Webhook added' });
      })
      .catch((error: unknown) => {
        addDangerNotification({ content: (error as Error).message });
      });
  };

  const handleTestEventTypeChange = (eventType: string) => {
    setValue('temporaryData', { ...temporaryData, testEventType: eventType });
  };

  const handleTestWebhook = () => {
    if (!currentUser) return;
    if (!temporaryData.testEventType) {
      addDangerNotification({ content: 'Select an event type first' });
      return;
    }
    handleSave(getValues(), () => {
      WebhookRepository.verify(id, { eventType: temporaryData.testEventType as EventType }, URLType === 'Sandbox')
        .then((response) => {
          setValue('temporaryData', { ...temporaryData, webhookResult: response });
          if (response.success) {
            addSuccessNotification({
              content: `${temporaryData.testEventType} successfully sent`,
              actionButton: {
                label: 'View Result',
                onClick: () => handleModalOpen(temporaryData.testEventType!, response),
              },
            });
            return;
          }
          addDangerNotification({
            content: `${response.responseCode} error`,
            actionButton: {
              label: 'View Result',
              onClick: () => handleModalOpen(temporaryData.testEventType!, response),
            },
          });
        })
        .catch((error: unknown) => {
          addDangerNotification({ content: (error as Error).message });
        });
    });
  };

  return (
    <>
      <WebhookResultModal
        isOpen={isWebhookResultModalOpen}
        onClose={() => setIsWebhookResultModalOpen(false)}
        webhookId={id}
        webhookResult={temporaryData.webhookResult}
        testableEvents={testableEvents}
        testEventType={modalTestEventType}
      />

      {currentPermission?.webhooks !== 'write' && (
        <Inner pb={0}>
          <RestrictedBanner />
        </Inner>
      )}
      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit, handleSubmitErrors)}>
          {RenderFields<WebhookFormValues>({
            sections: [
              {
                headline: 'General',
                fields: [
                  {
                    id: 'url',
                    label: 'URL',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
                      <Input
                        value={String(value)}
                        onChange={onChange}
                        onBlur={onBlur}
                        ref={ref}
                        placeholder="URL"
                        hasError={isTouched && !!error}
                      />
                    ),
                  },
                  {
                    id: 'temporaryData.testEventType',
                    label: 'Test',
                    hide: isCreate,
                    children: () => (
                      <Flex gap={8}>
                        <Flex auto>
                          <Dropdown
                            search
                            active={temporaryData.testEventType}
                            fullWidth
                            placeholder="Select event type"
                            options={testableEvents}
                            onChange={handleTestEventTypeChange}
                          />
                        </Flex>
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            handleTestWebhook();
                          }}
                          variant="secondary"
                          icon={<Icon.Bell />}
                        >
                          Send
                        </Button>
                        {temporaryData.webhookResult && (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => handleModalOpen(temporaryData.testEventType!)}
                            icon={<Icon.CircleInfo />}
                          >
                            Result
                          </Button>
                        )}
                      </Flex>
                    ),
                  },
                  {
                    id: 'description',
                    label: 'Description',
                    children: ({ value, onChange, onBlur, ref }, { isTouched, error }) => (
                      <Textarea
                        value={value !== undefined ? String(value) : ''}
                        onChange={onChange}
                        onBlur={onBlur}
                        ref={ref}
                        placeholder="Description"
                        hasError={isTouched && !!error}
                      />
                    ),
                  },
                  {
                    id: 'type',
                    label: 'Environment',
                    rules: { required: true },
                    children: ({ value, onChange }) =>
                      !isCreate ? (
                        <FormText>
                          <Chip size="large" icon={URLType === 'Live' ? <Icon.CircleCheck /> : <Icon.Sandbox />}>
                            {URLType}
                          </Chip>
                        </FormText>
                      ) : (
                        <StyledSegmentedControl
                          options={[
                            {
                              label: (
                                <>
                                  <Icon.CircleCheck />
                                  Production
                                </>
                              ),
                              value: 'production',
                            },
                            {
                              label: (
                                <>
                                  <Icon.Sandbox />
                                  Sandbox
                                </>
                              ),
                              value: 'sandbox',
                            },
                          ]}
                          active={value}
                          onOptionChange={onChange}
                          withIcon
                          isDisabled={!currentPlatform?.isLiveEnabled}
                        />
                      ),
                  },
                  {
                    id: 'enabledEvents',
                    label: 'Events',
                    children: ({ value }) => (
                      <List>
                        <SegmentedControl
                          options={[
                            { label: 'All', value: true },
                            { label: 'Custom', value: false },
                          ]}
                          active={allEvents}
                          onOptionChange={setAllEvents}
                        />
                        <div>
                          <Dropdown
                            search
                            fullWidth
                            label="Please select"
                            isDisabled={allEvents}
                            options={availableEvents.map((name: string) => ({
                              label: name,
                              onClick: () => {
                                const current = getValues('enabledEvents') ?? [];
                                setValue('enabledEvents', [...current, name]);
                              },
                            }))}
                          />
                        </div>
                        {Array.isArray(value)
                          ? value
                              .filter((eventId: string) => eventId !== '*')
                              .map((eventId: string) => (
                                <Button
                                  onClick={() => {
                                    setValue(
                                      'enabledEvents',
                                      (getValues('enabledEvents') ?? []).filter((e: string) => e !== eventId)
                                    );
                                  }}
                                  icon={<Icon.Cross />}
                                  isDisabled={allEvents}
                                  iconRight
                                  key={eventId}
                                >
                                  {eventId}
                                </Button>
                              ))
                          : null}
                      </List>
                    ),
                  },
                  {
                    id: 'secret',
                    label: 'Secret',
                    hide: isCreate,
                    children: ({ value, onChange }) => (
                      <HiddenInput
                        value={typeof value === 'string' ? value : ''}
                        placeholder="Secret Will be Generated"
                        onChange={onChange}
                      />
                    ),
                  },
                  {
                    id: 'isDisabled',
                    label: 'Status',
                    hide: isCreate,
                    children: ({ value, onChange }) => (
                      <SegmentedControl
                        active={value ?? false}
                        options={[
                          { label: 'Enabled', value: false },
                          { label: 'Disabled', value: true },
                        ]}
                        onOptionChange={onChange}
                      />
                    ),
                  },
                ],
              },
            ],
          })}
          <Inner px="var(--page-margin)">
            <EditToolbar>
              <Button onClick={() => navigate(-1)} variant="secondary" size="small">
                {id ? 'Back' : 'Cancel'}
              </Button>
              <Button
                type="submit"
                isDisabled={currentPermission?.webhooks !== 'write'}
                size="small"
                icon={!id && <Icon.Plus />}
              >
                {id ? 'Save Changes' : 'Create Webhook'}
              </Button>
            </EditToolbar>
          </Inner>
        </form>
      </FormProvider>
    </>
  );
};
